/**
 * MOBILE DESIGN TOKENS
 * 
 * Theme: Modern Land Stewardship
 * Professional land ownership and property management.
 * Grounded, calm, precise, quietly premium.
 * 
 * DO NOT use bright colors, gradients, or playful elements.
 * DO NOT use rustic or decorative farming motifs.
 */

// =============================================================================
// COLORS - Modern Land Stewardship Palette
// =============================================================================

export const color = {
  // Core Neutrals (80-90% of the UI)
  
  /** Pure white - primary background. Clean and modern. */
  background: '#FFFFFF',
  
  /** Soft slate - secondary background, separators. Modern and sophisticated. */
  backgroundSecondary: '#F8FAFC',
  
  /** Surface - cards, panels. Pure white for contrast. */
  surface: '#FFFFFF',
  
  /** Rich charcoal - primary text. Deep and readable. */
  textPrimary: '#0F172A',
  
  /** Slate gray - secondary text. Clear and modern. */
  textSecondary: '#475569',
  
  /** Cool gray - muted text. Subtle but visible. */
  textMuted: '#94A3B8',
  
  /** Inverse text - on dark/accent backgrounds. */
  textInverse: '#FFFFFF',
  
  // Primary Accent (Very Controlled - actions only)
  
  /** Vibrant forest green - primary actions, active states, key highlights. Rich and alive. */
  primary: '#16A34A',
  
  /** Primary hover/pressed state. Deep, rich green. */
  primaryDark: '#15803D',
  
  /** Primary subtle background (for selected states). Fresh, light green. */
  primaryLight: '#DCFCE7',
  
  // Secondary Accent (Rare - warnings/attention)
  
  /** Golden amber - warnings, attention states, status indicators. Warm and energetic. */
  warning: '#F97316',
  warningLight: '#FFEDD5',
  
  /** Error state - more urgent than warning. Bold and clear red. */
  error: '#DC2626',
  errorLight: '#FEE2E2',
  
  /** Success - vibrant green. Fresh and positive. */
  success: '#16A34A',
  successLight: '#DCFCE7',
  
  // Informational (Optional - maps, links)
  
  /** Electric blue - maps, info states. Bold and modern. */
  info: '#2563EB',
  infoLight: '#DBEAFE',
  
  // UI Elements
  
  /** Border - subtle, structural. Clean slate. */
  border: '#E2E8F0',
  
  /** Border strong - for emphasis. Clear separation. */
  borderStrong: '#CBD5E0',
  
  /** Divider - subtle separation. Light and clean. */
  divider: '#F1F5F9',
  
  /** Pressed state overlay. */
  pressed: 'rgba(0, 0, 0, 0.04)',
  
  /** Disabled elements. */
  disabled: '#94A3B8',
} as const;

// =============================================================================
// SPACING - 4pt grid
// =============================================================================

export const space = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

// =============================================================================
// TYPOGRAPHY - Serious, clear, editorial
// =============================================================================

export const text = {
  // Page title - 28pt Bold
  pageTitle: {
    fontSize: 28,
    fontWeight: '700' as const,
    lineHeight: 36,
    letterSpacing: -0.5,
  },
  
  // Section title - 20pt Semibold
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600' as const,
    lineHeight: 28,
    letterSpacing: -0.3,
  },
  
  // Card title - 17pt Semibold
  cardTitle: {
    fontSize: 17,
    fontWeight: '600' as const,
    lineHeight: 24,
    letterSpacing: -0.2,
  },
  
  // Body - 15pt Regular (generous line height)
  body: {
    fontSize: 15,
    fontWeight: '400' as const,
    lineHeight: 22,
    letterSpacing: 0,
  },
  
  // Body medium - 15pt Medium
  bodyMedium: {
    fontSize: 15,
    fontWeight: '500' as const,
    lineHeight: 22,
    letterSpacing: 0,
  },
  
  // Caption - 13pt Regular
  caption: {
    fontSize: 13,
    fontWeight: '400' as const,
    lineHeight: 18,
    letterSpacing: 0,
  },
  
  // Label - 12pt Medium, uppercase
  label: {
    fontSize: 12,
    fontWeight: '500' as const,
    lineHeight: 16,
    letterSpacing: 0.8,
    textTransform: 'uppercase' as const,
  },
  
  // Stat value - 32pt Bold
  statValue: {
    fontSize: 32,
    fontWeight: '700' as const,
    lineHeight: 40,
    letterSpacing: -0.5,
  },
  
  // Tab label - 10pt Medium
  tabLabel: {
    fontSize: 10,
    fontWeight: '500' as const,
    lineHeight: 12,
    letterSpacing: 0.2,
  },
} as const;

// =============================================================================
// TOUCH TARGETS
// =============================================================================

export const touch = {
  minimum: 44,
  recommended: 48,
  listRow: 56,
} as const;

// =============================================================================
// LAYOUT
// =============================================================================

export const layout = {
  /** Bottom tab bar height (content + typical safe area) */
  tabBarHeight: 83,
  /** Bottom padding to clear tab bar when scrolling */
  scrollBottomPadding: 100,
} as const;

// =============================================================================
// RADIUS - Softly rounded, not playful
// =============================================================================

export const radius = {
  none: 0,
  sm: 6,
  md: 10,
  lg: 14,
  full: 9999,
} as const;

// =============================================================================
// BORDERS - Subtle, structural (prefer borders over shadows)
// =============================================================================

export const border = {
  width: {
    thin: 1,
    medium: 1.5,
  },
} as const;

// =============================================================================
// SHADOWS - Minimal, grounded (avoid floating glass)
// =============================================================================

export const shadow = {
  none: {
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 2,
    elevation: 1,
  },
} as const;

// =============================================================================
// ICON SIZES
// =============================================================================

export const icon = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

// =============================================================================
// ANIMATION - Subtle, grounded (no bounce, no spring)
// =============================================================================

export const animation = {
  fast: 150,
  normal: 200,
  slow: 300,
} as const;
